// Tabs/OverviewTab.tsx
import { Stack, Group, Text, SimpleGrid, Card, Button, Skeleton, Avatar, Badge, Divider } from '@mantine/core';
import {
  IconMail, IconPhone, IconBriefcase2, IconUserMinus, IconMap, IconMapPin,
  IconHeartHandshake, IconVideo, IconUsers, IconId,
} from '@tabler/icons-react';
import { useLeaderService, LeaderModelView } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useState } from 'react';
import ConfirmActionModal from './ConfirmActionModal';

interface OverviewTabProps {
  leader: LeaderModelView | null;
  active: boolean;
  zoneCount: number;
  locationCount: number;
  needCount: number;
  onRemoved?: () => void;
  onTabChange?: (tab: string) => void;
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <div style={{ color: 'var(--mantine-color-dimmed)', marginTop: 2 }}>{icon}</div>
      <Stack gap={2}>
        <Text size="xs" c="dimmed">{label}</Text>
        <Text size="sm" fw={600}>{value}</Text>
      </Stack>
    </Group>
  );
}

function StatItem({
  icon,
  count,
  label,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  color: string;
  onClick?: () => void;
}) {
  return (
    <Stack
      gap={4}
      align="center"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : undefined }}
    >
      <Group gap={6} align="center">
        <div style={{ color: `var(--mantine-color-${color}-4)` }}>{icon}</div>
        <Text fw={700} c={color}>{count}</Text>
      </Group>
      <Text size="sm" c="dimmed">{label}</Text>
    </Stack>
  );
}

export default function OverviewTab({ leader, zoneCount, locationCount, needCount, onRemoved, onTabChange }: OverviewTabProps) {
  const Leaders = useLeaderService();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!leader) return null;

  const displayEmail = leader.email || 'N/A';
  const displayPhone = leader.phone || 'N/A';

  const supportModes = [
    leader.supportsInPerson ? 'In Person' : null,
    leader.supportsVirtual ? 'Virtual' : null,
  ].filter(Boolean).join(' & ') || 'Not specified';

  const handleRemoveLeader = () => setConfirmOpen(true);

  const confirmRemoveLeader = async () => {
    setRemoving(true);
    try {
      await Leaders.delete(leader.userId);
      toast.warning('Leader Removed', 'Leader has been removed.');
      onRemoved?.();
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to remove leader.');
    } finally {
      setRemoving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Stack gap="lg">
      <ConfirmActionModal
        opened={confirmOpen}
        title="Remove Leader"
        message="Are you sure you want to remove this leader as a care support leader? This action cannot be undone."
        onConfirm={confirmRemoveLeader}
        onClose={() => setConfirmOpen(false)}
      />

      {loading ? (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Skeleton height={220} radius="md" />
          <Skeleton height={220} radius="md" />
        </SimpleGrid>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          {/* ── Leader Details ── */}
          <Card withBorder radius="md" padding="lg">
            <Text fw={700} mb="sm">Leader Details</Text>
            <Divider mb="md" />
            <Stack gap="md">
              <DetailRow icon={<IconMail size={18} />} label="Email" value={displayEmail} />
              <DetailRow icon={<IconPhone size={18} />} label="Phone" value={displayPhone} />
              <DetailRow
                icon={<IconBriefcase2 size={18} />}
                label="Active Cases"
                value={leader.activeCaseCount ?? 0}
              />
            </Stack>
          </Card>

          {/* ── Support & Assignment ── */}
          <Card withBorder radius="md" padding="lg">
            <Text fw={700} mb="sm">Support & Assignment</Text>
            <Divider mb="md" />
            <Stack gap="md">
              <DetailRow
                icon={<IconUsers size={18} />}
                label="Support Mode"
                value={supportModes}
              />
              <DetailRow
                icon={<IconMap size={18} />}
                label="Zones"
                value={
                  leader.zones?.length
                    ? (
                      <Group gap={4}>
                        {leader.zones.map((z) => (
                          <Badge
                            key={z.leaderZoneID}
                            size="sm"
                            variant="light"
                            style={{
                              backgroundColor: z.zoneColor ? `${z.zoneColor}22` : undefined,
                              color: z.zoneColor ?? undefined,
                            }}
                          >
                            {z.zoneName}
                          </Badge>
                        ))}
                      </Group>
                    )
                    : 'None assigned'
                }
              />
              <DetailRow
                icon={<IconMapPin size={18} />}
                label="Locations"
                value={
                  leader.locations?.length
                    ? leader.locations.map((l) => l.locationName).join(', ')
                    : 'None assigned'
                }
              />
            </Stack>
          </Card>
        </SimpleGrid>
      )}

      {/* ── Bottom stat bar (Zones / Locations / Needs) ── */}
      <Card withBorder radius="md" padding="lg">
        <Group justify="space-around">
          <StatItem
            icon={<IconMap size={20} />}
            count={zoneCount}
            label="Zones"
            color="blue"
            onClick={() => onTabChange?.('zones')}
          />
          <StatItem
            icon={<IconMapPin size={20} />}
            count={locationCount}
            label="Locations"
            color="grape"
            onClick={() => onTabChange?.('locations')}
          />
          <StatItem
            icon={<IconHeartHandshake size={20} />}
            count={needCount}
            label="Needs"
            color="pink"
            onClick={() => onTabChange?.('counseling')}
          />
        </Group>
      </Card>

      <Group justify="flex-end">
        <Button
          color="red"
          variant="light"
          leftSection={<IconUserMinus size={16} />}
          loading={removing}
          onClick={handleRemoveLeader}
        >
          Remove Leader
        </Button>
      </Group>
    </Stack>
  );
}